import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { CredentialsDialog } from '@/components/admins/CredentialsDialog';
import { Page, PageHeader } from '@/components/layout/PageHeader';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FormAlert } from '@/components/ui/form-alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminKeys, adminsApi, type CreateAdminResponse } from '@/lib/admins-api';
import { genericErrorMessage, httpStatus, serverCode } from '@/lib/api-errors';

// Mêmes contraintes que createSchema (platform-api, routes/admins.ts)
export const createAdminSchema = z.object({
  email: z.string().trim().min(1, "L'email est obligatoire").email("Format d'email invalide").max(254),
  firstName: z.string().trim().min(1, 'Prénom requis').max(60, '60 caractères maximum'),
  lastName: z.string().trim().min(1, 'Nom requis').max(60, '60 caractères maximum'),
});

export type CreateAdminForm = z.infer<typeof createAdminSchema>;

const FormField = ({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    {children}
    {error && (
      <p id={`${id}-error`} className="text-xs text-danger">
        {error}
      </p>
    )}
  </div>
);

export const AdminNewPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  // Réponse 201 (secrets en clair) : state local seulement, effacé à la fermeture de la modal
  const [created, setCreated] = useState<CreateAdminResponse | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CreateAdminForm>({
    resolver: zodResolver(createAdminSchema),
    defaultValues: { email: '', firstName: '', lastName: '' },
  });

  const mutation = useMutation({
    mutationFn: adminsApi.create,
    onSuccess: (data) => {
      setCreated(data);
      void queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
    onError: (err) => {
      if (serverCode(err) === 'ADMIN_EMAIL_TAKEN') setError('email', { message: 'Cet email est déjà utilisé' }, { shouldFocus: true });
      else if (httpStatus(err) === 400) setFormError('Données refusées par le serveur, vérifiez le formulaire');
      else setFormError(genericErrorMessage(err));
    },
  });

  const onSubmit = (form: CreateAdminForm) => {
    setFormError(null);
    mutation.mutate({ email: form.email.trim().toLowerCase(), firstName: form.firstName.trim(), lastName: form.lastName.trim() });
  };

  const close = () => {
    const id = created!.admin.id;
    setCreated(null);
    // La mutation garde sa réponse (secrets compris) dans le MutationCache tant qu'elle n'est pas remise à zéro
    mutation.reset();
    void navigate({ to: '/admins/$id', params: { id }, replace: true });
  };

  const aria = (name: keyof CreateAdminForm) => ({
    'aria-invalid': Boolean(errors[name]),
    'aria-describedby': errors[name] ? `${name}-error` : undefined,
  });

  return (
    <Page className="max-w-2xl">
      <Link to="/admins" className={buttonVariants({ variant: 'ghost', size: 'sm', className: '-ml-3 w-fit text-muted-foreground' })}>
        <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
        Admins
      </Link>
      <PageHeader
        title="Créer un admin"
        description="Le mot de passe initial et le MFA sont générés par le serveur et affichés une seule fois. L'admin devra changer son mot de passe à la première connexion."
      />

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
        <Card>
          <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <FormField id="email" label="Email" error={errors.email?.message}>
                <Input id="email" type="email" autoComplete="off" autoFocus placeholder="prenom.nom@logistech-lgt.com" {...register('email')} {...aria('email')} />
              </FormField>
            </div>
            <FormField id="firstName" label="Prénom" error={errors.firstName?.message}>
              <Input id="firstName" autoComplete="off" maxLength={60} {...register('firstName')} {...aria('firstName')} />
            </FormField>
            <FormField id="lastName" label="Nom de famille" error={errors.lastName?.message}>
              <Input id="lastName" autoComplete="off" maxLength={60} {...register('lastName')} {...aria('lastName')} />
            </FormField>
          </CardContent>
        </Card>

        {formError && <FormAlert>{formError}</FormAlert>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Link to="/admins" className={buttonVariants({ variant: 'outline' })}>
            Annuler
          </Link>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            {mutation.isPending ? 'Création…' : "Créer l'admin"}
          </Button>
        </div>
      </form>

      <CredentialsDialog
        title="Admin créé"
        email={created?.admin.email ?? ''}
        credentials={created?.initialCredentials ?? null}
        onClose={close}
      />
    </Page>
  );
};
